import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  UploadCloud,
  Download,
  RefreshCw,
  FileText,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileSpreadsheet,
  ChevronRight,
  ChevronLeft,
  Trash2,
  CheckSquare,
  Square,
  ChevronUp,
  ChevronDown,
  Search,
  X,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Loader } from '../../../components/ui/Loader';
import { WarehouseRow } from '../../../components/WarehouseRow';
import Tooltip from '../../../components/ui/Tooltip';
import { cn } from '../../../utils/cn';
import { Checkbox } from '../../../components/ui/Checkbox';
import * as XLSX from 'xlsx';
import { useAuth } from '../../../hooks/useAuth';
import { useAddress } from '../../../hooks/useAddress';
// @ts-ignore
import ExcelJS from 'exceljs';
import { useModal } from '../../../hooks/useModal';
import {
  parseBulkFile,
  validateBulkOrders,
  ValidatedBulkOrder,
  ValidationError,
} from '../../../utils/bulkUpload';
import { BulkProcessModal } from './BulkProcessModal';
import { bulkService } from '../../../services/bulk';

// Mock data for initial table
interface UploadHistoryItem {
  id: number;
  no: number;
  date: string;
  fileName: string;
  totalOrders: number;
  successfulOrders: number;
  errorOrders: number;
  status: 'Successful' | 'Unsuccessful' | 'Processing' | 'Partial';
}

export const BulkOrdersPage: React.FC = () => {
  const [view, setView] = useState<'history' | 'preview'>('history');
  const [history, setHistory] = useState<UploadHistoryItem[]>([]);
  const [previewData, setPreviewData] = useState<ValidatedBulkOrder[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [highlightedIdx, setHighlightedIdx] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const { user } = useAuth();
  const { data: addressData } = useAddress(user?.id as string);
  const { open: openModal, close: closeModal, Modal } = useModal();
  const location = useLocation();

  const fetchHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const data = await bulkService.getBulkUploadHistory();
      const mapped = data.map((item, index) => {
        let statusStr: 'Successful' | 'Unsuccessful' | 'Processing' | 'Partial' = 'Successful';
        if (item.status === 0) statusStr = 'Processing';
        else if (item.status === 2) statusStr = 'Unsuccessful';
        else if (item.status === 3) statusStr = 'Partial';

        return {
          id: item.id,
          no: index + 1,
          date: item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-IN') : '-',
          fileName: item.fileName,
          totalOrders: item.totalOrders || 0,
          successfulOrders: item.successfulOrders || 0,
          errorOrders: item.errorOrders || 0,
          status: statusStr,
        };
      });
      setHistory(mapped);
    } catch (err) {
      console.error('Failed to fetch bulk upload history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleDownload = async (id: number) => {
    try {
      const response = await bulkService.downloadProcessedBulkFile(id);
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `processed_bulk_${id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    if (location.state?.orders && Array.isArray(location.state.orders)) {
      const selectedOrders = location.state.orders;

      const mappedRows = selectedOrders.map((o: any) => ({
        orderId: o.order_number,
        receiverName: o.destination_address?.contact_name || '',
        receiverPhone: o.destination_address?.contact_no || '',
        receiverEmail: o.destination_address?.email || '',
        address: o.destination_address?.address1 || '',
        address2: o.destination_address?.address2 || '',
        landmark: o.destination_address?.landmark || '',
        pincode: o.destination_address?.pincode || '',
        city: o.destination_address?.city || '',
        state: o.destination_address?.state || '',
        weight: o.dead_weight || 0.5,
        length: o.length || 10,
        width: o.breadth || 10,
        height: o.height || 10,
        productName: o.package_desc || 'Product',
        quantity: o.quantity || 1,
        invoiceValue: o.shipment_value || 0,
        paymentMode:
          o.payment_mode?.toLowerCase() === 'cod' ? 'COD' : 'Prepaid',
        pickupDate: new Date().toISOString().split('T')[0],
        sku: o.sku || '',
        hsn: o.hsn || '',
        altPhone: o.destination_address?.alt_contact_no || '',
      }));

      const validated = validateBulkOrders(mappedRows);
      setPreviewData(validated);
      setView('preview');
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const toggleSelectAll = () => {
    if (selectedIndices.length === previewData.length) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(previewData.map((_, idx) => idx));
    }
  };

  const toggleSelect = (idx: number) => {
    setSelectedIndices((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  };

  const deleteSelected = () => {
    if (selectedIndices.length === 0) return;
    const newData = previewData.filter(
      (_, idx) => !selectedIndices.includes(idx)
    );
    setPreviewData(newData);
    setSelectedIndices([]);
    toast.success(`Removed ${selectedIndices.length} orders`);
  };

  const handleDeleteRow = (idx: number) => {
    setPreviewData((prev) => prev.filter((_, i) => i !== idx));
    setSelectedIndices((prev) =>
      prev.filter((i) => i !== idx).map((i) => (i > idx ? i - 1 : i))
    );
    toast.success('Order removed');
  };

  const jumpToErrorRow = (direction: 'next' | 'prev') => {
    const errorIndices = previewData
      .map((r, i) => (!r.isValid ? i : -1))
      .filter((i) => i !== -1);

    if (errorIndices.length === 0) return;

    let targetIdx: number;
    const currentHighlightPos =
      highlightedIdx !== null ? errorIndices.indexOf(highlightedIdx) : -1;

    if (direction === 'next') {
      targetIdx = errorIndices[(currentHighlightPos + 1) % errorIndices.length];
    } else {
      targetIdx =
        errorIndices[
          (currentHighlightPos - 1 + errorIndices.length) % errorIndices.length
        ];
    }

    setHighlightedIdx(targetIdx);
    rowRefs.current[targetIdx]?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    // Auto-clear highlight
    setTimeout(() => {
      setHighlightedIdx(null);
    }, 2000);
  };

  const hasErrors = useMemo(
    () => previewData.some((r) => !r.isValid),
    [previewData]
  );
  const canProceed = useMemo(
    () => previewData.length > 0 && !hasErrors,
    [previewData, hasErrors]
  );

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setIsProcessing(true);
    const toastId = toast.success('Processing file...');

    try {
      const rows = await parseBulkFile(file);
      const validated = validateBulkOrders(rows);
      setPreviewData(validated);

      toast.success('File processed successfully!', { id: toastId });

      // Auto-open modal
      openModal({
        element: (
          <BulkProcessModal
            orders={validated}
            addressData={addressData}
            file={file}
            onClose={closeModal}
          />
        ),
        className: 'w-screen h-screen max-w-none m-0 rounded-none',
        closable: true,
      });
    } catch (error) {
      console.error('Error parsing file:', error);
      toast.error(
        'Failed to parse file. Please ensure it is a valid CSV or Excel file.',
        { id: toastId }
      );
    } finally {
      setIsProcessing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadSample = async () => {
    const headers = [
      'Order ID',
      'Receiver Name',
      'Receiver Phone',
      'Receiver Email',
      'Address',
      'Address 2',
      'Landmark',
      'Pincode',
      'City',
      'State',
      'Weight (kg)',
      'Length (cm)',
      'Width (cm)',
      'Height (cm)',
      'Product Name',
      'Quantity',
      'Invoice Value',
      'SKU',
      'HSN',
      'Payment Mode',
      'Pickup Date',
      'Alt Phone',
      'Address Nickname',
      'Pickup Contact Name',
      'Pickup Phone',
      'Pickup Address',
      'Pickup Address 2',
      'Pickup Landmark',
      'Pickup Pincode',
      'Pickup City',
      'Pickup State',
    ];

    const nonMandatoryColumns = [
      'Receiver Email', 'Address 2', 'SKU', 'HSN', 'Pickup Date', 'Alt Phone', 
      'Pickup Address 2'
    ];

    const dummyDataPrepaid = [
      'ORD001',
      'John Doe',
      '9876543210',
      'john@example.com',
      '123 Street Name',
      'Building 4B',
      'Near Metro Station',
      '400001',
      'Mumbai',
      'Maharashtra',
      '0.5',
      '10',
      '10',
      '10',
      'Sample Product (Prepaid)',
      '1',
      '500',
      'SKU123',
      'HSN456',
      'Prepaid',
      new Date().toISOString().split('T')[0],
      '',
      'Main Warehouse',
      'Rajesh Verma',
      '9123456789',
      '45 Warehouse Rd',
      'Industrial Area',
      'Near Cargo Gate',
      '400002',
      'Mumbai',
      'Maharashtra',
    ];

    const dummyDataCod = [
      'ORD002',
      'Jane Smith',
      '9876543211',
      '', // Optional Email
      '456 Park Avenue',
      'Flat 2A',
      'Opposite City Mall',
      '110001',
      'New Delhi',
      'Delhi',
      '1.0',
      '15',
      '12',
      '10',
      'Sample Product (COD)',
      '1',
      '850',
      'SKU789',
      'HSN456',
      'COD',
      new Date().toISOString().split('T')[0],
      '9876500000',
      'Main Warehouse',
      'Rajesh Verma',
      '9123456789',
      '45 Warehouse Rd',
      'Industrial Area',
      'Near Cargo Gate',
      '400002',
      'Mumbai',
      'Maharashtra',
    ];

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sample Orders');

    // Add headers
    const headerRow = worksheet.addRow(headers);
    
    // Style headers
    headerRow.eachCell((cell: any, colNumber: number) => {
      const headerName = headers[colNumber - 1];
      
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF008080' } // Teal (#008080) for all
      };
      cell.font = {
        bold: true,
        color: { argb: 'FFFFFFFF' } // White text on teal
      };
      
      // Auto-width adjustment roughly
      const column = worksheet.getColumn(colNumber);
      column.width = Math.max(15, headerName.length + 5);
    });

    // Add dummy data (both Prepaid and COD)
    worksheet.addRow(dummyDataPrepaid);
    worksheet.addRow(dummyDataCod);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', 'sample-orders-template.xlsx');
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getCellContent = (
    row: ValidatedBulkOrder,
    field: keyof ValidatedBulkOrder
  ) => {
    const error = row.errors.find((e) => e.column === field);
    let value = row[field as keyof ValidatedBulkOrder];

    if (field === 'pickupDate' && value) {
      // Just in case it's still serial, but formatExcelDate should have handled it
      // Still, we can ensure it's not showing as a long float
      if (!isNaN(Number(value)) && String(value).includes('.')) {
        // This shouldn't happen if formatExcelDate worked, but safety first
      }
    }

    if (error) {
      return (
        <div className="flex flex-col items-center text-center">
          <span className="text-red-600 font-medium">
            {String(value || 'Empty')}
          </span>
          <span className="text-xs text-red-500 italic leading-none">
            {error.message}
          </span>
        </div>
      );
    }
    return <span>{String(value)}</span>;
  };

  const handleGetRates = () => {
    if (!canProceed) {
      if (hasErrors) {
        toast.error('Please remove all orders with errors before proceeding.');
      } else if (previewData.length === 0) {
        toast.error('No orders to process.');
      }
      return;
    }

    openModal({
      element: (
        <BulkProcessModal
          orders={previewData}
          addressData={addressData}
          file={uploadedFile || undefined}
          onClose={closeModal}
        />
      ),
      className: 'w-screen h-screen max-w-none m-0 rounded-none',
      closable: true,
    });
  };

  return (
    <div className="min-h-screen py-6 ">
      <div className="mx-auto space-y-6 sm:px-4 md:px-4 lg:px-0 ">
        {/* Header */}
        <div className="flex flex-col">
          <h1 className="text-2xl  font-bold text-gray-900 tracking-tight">
            {view === 'history' ? 'Bulk Upload' : 'Validate Orders'}
          </h1>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span>
              {' '}
              Schedule bulk shipments for your customers by uploading an Excel
              file{' '}
            </span>
          </div>

          {view === 'preview' && (
            <Button
              variant="ghost"
              onClick={() => setView('history')}
              className="flex items-center gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to History
            </Button>
          )}
        </div>

        {/* Upload Action Box */}
        <Card className="p-6 mx-4 border-dashed border-2 border-gray-300 bg-white/50 backdrop-blur-sm">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="flex-1 text-center lg:text-left">
              <p className="text-sm text-gray-600 leading-relaxed max-w-2xl">
                Download the sample file and replace its data with your order
                data. Save the file and upload it back. Make sure all mandatory
                fields are filled in the sheet. Only CSV file format will be
                accepted.
              </p>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-4 shrink-0">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept=".csv, .xlsx, .xls"
                onChange={handleFileUpload}
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isProcessing}
                className="hover:bg-[#004d4d] text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-all shadow-sm"
              >
                <UploadCloud className="h-5 w-5" />
                {isProcessing ? 'Processing...' : 'Upload File'}
              </Button>

              <div
                onClick={downloadSample}
                className="flex items-center gap-3 px-4 py-2 border-2 border-gray-200 rounded-xl cursor-pointer hover:bg-gray-50 transition-colors group bg-white shadow-sm"
              >
                <div className="bg-green-100 p-1.5 rounded-lg group-hover:scale-110 transition-transform">
                  <img
                    src="/excel.png"
                    alt="Excel logo"
                    className="w-12 h-12"
                  />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-gray-900">
                    Sample File
                  </span>
                  <span className="text-xs text-gray-500">
                    Download Template
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {view === 'history' ? (
          <div className="space-y-4">
            <div className="flex justify-end pr-2">
              <button
                onClick={fetchHistory}
                disabled={isLoadingHistory}
                className="flex items-center gap-1.5 text-sm font-medium text-[#006060] hover:underline transition-all disabled:opacity-50"
              >
                <RefreshCw className={cn("h-4 w-4", isLoadingHistory && "animate-spin")} />
                {isLoadingHistory ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {/* History Table Header */}
            <div className="hidden lg:block bg-teal text-white rounded-xl px-4 py-3 shadow-md bg-teal border rounded-xl px-3 xl:px-5 py-2.5 text-xs xl:text-sm font-bold text-white shadow-md">
              <div className="grid grid-cols-[50px_110px_1fr_100px_130px_130px_120px_60px] gap-4 items-center text-center ">
                <div className="text-left pl-2">No</div>
                <div>Date</div>
                <div className="text-left">File Name</div>
                <div>Orders</div>
                <div>Successful</div>
                <div>Error</div>
                <div>Status</div>
                <div>Action</div>
              </div>
            </div>

            {/* History List */}
            <div className="hidden lg:block space-y-3">
              {history.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all"
                >
                  <div className="grid grid-cols-[50px_110px_1fr_100px_130px_130px_120px_60px] gap-4 items-center text-center text-sm">
                    <div className="text-left pl-2 text-gray-500 font-medium">
                      {item.no}.
                    </div>
                    <div className="text-gray-700">{item.date}</div>
                    <div className="text-left text-gray-900 font-medium truncate pr-4">
                      {item.fileName}
                    </div>
                    <div className="font-semibold text-gray-800">
                      {item.totalOrders}
                    </div>
                    <div className="font-semibold text-green-600">
                      {item.successfulOrders}
                    </div>
                    <div className="font-semibold text-red-600">
                      {item.errorOrders}
                    </div>
                    <div>
                      <span
                        className={cn(
                          'px-3 py-1 rounded-full text-xs font-bold ',
                          item.status === 'Successful'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        )}
                      >
                        {item.status}
                      </span>
                    </div>
                    <div>
                      <button 
                        onClick={() => handleDownload(item.id)}
                        className="p-2 text-[#006060] hover:bg-[#00606010] rounded-lg transition-colors"
                      >
                        <Download className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Mobile Grid View (Hidden on large) */}
            <div className="lg:hidden space-y-4 px-4 sm:px-4 md:px-4 lg:px-0">
              {history.map((item) => (
                <Card key={item.no} className="p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-gray-900">
                        #{item.no}
                      </span>
                      <span className="text-xs text-gray-500">{item.date}</span>
                    </div>
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-bold',
                        item.status === 'Successful'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      )}
                    >
                      {item.status}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 line-clamp-1">
                    {item.fileName}
                  </p>
                  <div className="grid grid-cols-3 gap-2 py-2 border-y border-gray-100">
                    <div className="flex flex-col items-center">
                      <span className="text-xs text-gray-500">Orders</span>
                      <span className="font-bold">{item.totalOrders}</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs text-green-600">Success</span>
                      <span className="font-bold text-green-600">
                        {item.successfulOrders}
                      </span>
                    </div>
                    <div className="flex flex-col items-center">
                      <span className="text-xs text-red-600">Error</span>
                      <span className="font-bold text-red-600">
                        {item.errorOrders}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-end pt-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(item.id)}
                      className="h-8 text-[#006060]"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ) : (
          /* Preview View */
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex gap-4">
                <div className="bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                  <span className="text-sm text-green-700 font-medium">
                    Valid: {previewData.filter((r) => r.isValid).length}
                  </span>
                </div>
                <div className="bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                  <span className="text-sm text-red-700 font-medium">
                    Errors: {previewData.filter((r) => !r.isValid).length}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-4 w-full md:w-auto">
                {hasErrors && (
                  <div className="flex gap-1">
                    <Tooltip text="Jump to previous error" side="bottom">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => jumpToErrorRow('prev')}
                        className="h-10 w-10 p-0 bg-white border shadow-sm hover:bg-red-50 text-red-600 rounded-lg"
                      >
                        <ChevronUp className="h-5 w-5" />
                      </Button>
                    </Tooltip>
                    <Tooltip text="Jump to next error" side="bottom">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => jumpToErrorRow('next')}
                        className="h-10 w-10 p-0 bg-white border shadow-sm hover:bg-red-50 text-red-600 rounded-lg"
                      >
                        <ChevronDown className="h-5 w-5" />
                      </Button>
                    </Tooltip>
                  </div>
                )}
                {selectedIndices.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={deleteSelected}
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete ({selectedIndices.length})
                  </Button>
                )}
              </div>
            </div>

            {hasErrors && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-xl flex items-center gap-3 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm font-medium">
                  Some orders have errors. You must delete all red-highlighted
                  rows to proceed with rate selection.
                </p>
              </div>
            )}

            {/* Preview Header */}
            <div className="hidden lg:block bg-teal text-white rounded-xl px-4 py-3 shadow-md">
              <div className="grid grid-cols-[40px_100px_120px_110px_1fr_160px_80px_100px_85px_60px_60px_60px] gap-2 items-center text-center text-xs font-bold tracking-wider">
                <div className="flex justify-center">
                  <Checkbox
                    checked={
                      selectedIndices.length === previewData.length &&
                      previewData.length > 0
                    }
                    onChange={toggleSelectAll}
                  />
                </div>
                <div>Order ID</div>
                <div>Name</div>
                <div>Phone</div>
                <div>Address</div>
                <div>City/State</div>
                <div>Weight</div>
                <div>Dimensions</div>
                <div>Pickup Date</div>
                <div>Payment</div>
                <div>Status</div>
                <div className="sr-only">Delete</div>
              </div>
            </div>

            {/* Preview List - Desktop */}
            <div className="hidden lg:block space-y-3">
              {previewData.map((row, idx) => (
                <div
                  key={idx}
                  ref={(el: HTMLDivElement | null) =>
                    (rowRefs.current[idx] = el)
                  }
                  className={cn(
                    'bg-white border rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all divide-x-reverse',
                    !row.isValid
                      ? 'border-red-200 bg-red-50/10'
                      : 'border-gray-100',
                    selectedIndices.includes(idx) ? 'ring-2 ring-teal/50' : '',
                    highlightedIdx === idx
                      ? 'ring-4 ring-yellow-400 scale-[1.01] z-10 bg-yellow-50/50'
                      : ''
                  )}
                >
                  <div className="grid grid-cols-[40px_100px_120px_110px_1fr_160px_80px_100px_85px_60px_60px_60px] gap-2 items-center text-center text-xs">
                    <div className="flex justify-center">
                      <Checkbox
                        checked={selectedIndices.includes(idx)}
                        onChange={() => toggleSelect(idx)}
                      />
                    </div>
                    <div className="font-semibold text-gray-900 truncate max-w-[50px]" title={String(row.orderId ?? '')}>
                      {getCellContent(row, 'orderId')}
                    </div>
                    <div className="font-medium text-gray-700">
                      {getCellContent(row, 'receiverName')}
                    </div>
                    <div className="text-gray-700">
                      {getCellContent(row, 'receiverPhone')}
                    </div>
                    <div className="truncate pr-2">
                      {getCellContent(row, 'address')}
                    </div>
                    <div className="font-medium">
                      <div className="flex flex-col items-center">
                        <span>{String(row.city)}</span>
                        <span className="text-[10px] text-gray-400 ">
                          {String(row.state)}
                        </span>
                      </div>
                    </div>
                    <div className="font-semibold text-teal">
                      {row.weight} kg
                    </div>
                    <div className="text-gray-500">
                      {row.length}x{row.width}x{row.height}
                    </div>
                    <div className="font-medium text-gray-700">
                      {getCellContent(row, 'pickupDate')}
                    </div>
                    <div>
                      <span
                        className={cn(
                          'px-2 py-0.5 rounded text-xs font-bold ',
                          row.paymentMode === 'COD'
                            ? 'bg-orange-100 text-orange-700'
                            : 'bg-blue-100 text-blue-700'
                        )}
                      >
                        {row.paymentMode}
                      </span>
                    </div>
                    <div className="flex justify-center">
                      {!row.isValid ? (
                        <XCircle className="h-5 w-5 text-red-500" />
                      ) : (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                    </div>
                    <div className="flex justify-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteRow(idx);
                        }}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-100 rounded-lg transition-all"
                      >
                        <Trash2 className="h-6 w-6 text-teal" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Preview List - Mobile */}
            <div className="lg:hidden space-y-4">
              {previewData.map((row, idx) => (
                <Card
                  key={idx}
                  ref={(el: HTMLDivElement | null) =>
                    (rowRefs.current[idx] = el)
                  }
                  className={cn(
                    'p-4 border shadow-sm transition-all relative',
                    !row.isValid
                      ? 'border-red-200 bg-red-50/10'
                      : 'border-gray-100',
                    selectedIndices.includes(idx) ? 'ring-2 ring-teal' : '',
                    highlightedIdx === idx
                      ? 'ring-4 ring-yellow-400 scale-[1.01] z-10 bg-yellow-50/50'
                      : ''
                  )}
                  onClick={() => toggleSelect(idx)}
                >
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteRow(idx);
                    }}
                    className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={selectedIndices.includes(idx)}
                        onChange={(e) => {
                          e.stopPropagation();
                          toggleSelect(idx);
                        }}
                      />
                      <span className="font-bold text-sm">#{row.orderId}</span>
                    </div>
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded text-xs font-bold ',
                        row.paymentMode === 'COD'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-blue-100 text-blue-700'
                      )}
                    >
                      {row.paymentMode}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-xs font-medium text-gray-900">
                      <p>{row.receiverName}</p>
                      <span className="text-gray-400 font-normal">|</span>
                      <p>{row.receiverPhone}</p>
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {row.address}
                    </p>
                    <div className="flex justify-between items-end mt-2">
                      <div className="text-xs text-gray-400">
                        {row.city}, {row.state}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Weight</p>
                          <p className="text-xs font-bold text-teal">
                            {row.weight} kg
                          </p>
                        </div>
                        <div className="flex items-center h-full">
                          {!row.isValid ? (
                            <XCircle className="h-5 w-5 text-red-500" />
                          ) : (
                            <CheckCircle2 className="h-5 w-5 text-green-500" />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {/* Bottom Actions */}
            <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
              <Button onClick={handleGetRates} disabled={!canProceed}>
                {hasErrors ? 'fix Errors to Proceed' : 'Get Rates'}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Modal />
    </div>
  );
};

export default BulkOrdersPage;
